import SelectField from '@pelckmans/elementis/components/select-field';
import SmartschoolIcon from '@pelckmans/elementis/icons/smartschool';
import debounce from 'lodash/debounce';
import { FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchUserSchools, School, UserSchool } from '../../../api/schools';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { fetchSchools } from '../api/MyGroupsApi';

const renderSchoolSubText = ({ streetName, streetNumber, zipCode, city }: School['address']) => {
  return `${streetName} ${streetNumber}, ${zipCode} ${city}`;
};

type Props = {
  onChange: (school: UserSchool | null) => void;
  onBlur: FocusEventHandler<HTMLElement>;
  value: UserSchool | null;
  errorText?: string;
  onlySmartschool?: boolean;
};

function SchoolSearchSelector({ onChange, onBlur, value, errorText, onlySmartschool = false }: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  const [isLoading, setIsLoading] = useState(true);
  const [userSchools, setUserSchools] = useState<UserSchool[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [searchValue, onSearchValueChange] = useState('');

  const schoolsJsx = useMemo(() => {
    if (userSchools.length === 0 && schools.length === 0) {
      return [];
    }

    if (userSchools.length === 0 && schools.length > 0) {
      return schools.map(school => (
        <SelectField.Item
          key={school.id}
          value={school}
          selected={value?.id === school.id}
          text={school.name}
          subText={renderSchoolSubText(school.address)}
        />
      ));
    }

    return (
      <>
        {Boolean(userSchools.length) && (
          <SelectField.Group title={t('PAGES.MY_GROUPS.SCHOOL.LISTS.MY_SCHOOLS.TITLE')}>
            {userSchools.map(userSchool => (
              <SelectField.Item
                key={userSchool.id}
                value={userSchool}
                selected={value?.id === userSchool.id}
                text={userSchool.name}
                subText={userSchool.isSmartschool ? `${userSchool.domain}` : renderSchoolSubText(userSchool.address)}
                subTextIcon={userSchool.isSmartschool ? <SmartschoolIcon /> : undefined}
              />
            ))}
          </SelectField.Group>
        )}
        {Boolean(schools.length) && (
          <SelectField.Group title={t('PAGES.MY_GROUPS.SCHOOL.LISTS.SCHOOLS.TITLE')}>
            {schools.map(school => (
              <SelectField.Item
                key={school.id}
                value={school}
                selected={value?.id === school.id}
                text={school.name}
                subText={renderSchoolSubText(school.address)}
              />
            ))}
          </SelectField.Group>
        )}
      </>
    );
  }, [schools, t, userSchools, value?.id]);

  const fetchLinkableSchoolsMatchingSearch = debounce(async (search?: string) => {
    try {
      if (!onlySmartschool) {
        const schoolsMatchingSearch = await fetchSchools(search);
        setSchools(schoolsMatchingSearch);
      }

      const userSchoolsMatchingSearch = await fetchUserSchools(search);
      if (!onlySmartschool) {
        setUserSchools(userSchoolsMatchingSearch);
      } else {
        setUserSchools(userSchoolsMatchingSearch.filter(school => school.isSmartschool));
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  useEffect(() => {
    fetchLinkableSchoolsMatchingSearch(searchValue || undefined);

    return () => fetchLinkableSchoolsMatchingSearch.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // Auto-select school if user has only one school and no school is currently selected
  useEffect(() => {
    if (userSchools.length === 1 && !value && !isLoading) {
      onChange(userSchools[0]);
    }
  }, [userSchools, value, isLoading, onChange]);

  return (
    <SelectField
      id="schools"
      required
      searchable
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={!!errorText}
      errorText={errorText}
      isLoading={isLoading}
      renderValueContent={option => option.name}
      placeholder={t('PAGES.MY_GROUPS.SCHOOL.PLACEHOLDER')}
      labelText={t('PAGES.MY_GROUPS.SCHOOL.LABEL')}
      dropdownMenuProps={{
        ariaLabel: t('PAGES.MY_GROUPS.SCHOOL.A11Y_LABEL'),
      }}
      searchProps={{
        searchValue,
        onSearchValueChange,
        inputProps: {
          ariaLabel: t('PAGES.MY_GROUPS.SCHOOL.SEARCH.A11Y_LABEL'),
        },
      }}
      flip
    >
      {schoolsJsx}
    </SelectField>
  );
}

export default SchoolSearchSelector;
